/**
 * A refused write, said where the control that earned it is (ADR 000130).
 *
 * A panel keeps one refusal and tags it with the place it belongs to; each
 * place renders one of these and it draws only its own. That keeps the answer
 * beside the button on a panel tall enough that its top is off-screen from
 * most of its controls.
 */
export interface Refusal<Place extends string> {
  where: Place;
  message: string;
}

interface RefusalLineProps<Place extends string> {
  refusal: Refusal<Place> | null;
  /** The place this line speaks for. */
  at: Place;
  /**
   * Draw a `span` rather than a `p`, for the places that already sit inside a
   * paragraph and cannot hold a block.
   */
  inline?: boolean;
}

/**
 * `role="alert"` is the point of this component as much as the placement is. A
 * button that re-enables looks identical whether the write landed or not, so
 * without the announcement somebody who cannot see the line appear is told
 * nothing at all about a refusal (ADR 000130).
 */
export function RefusalLine<Place extends string>({
  refusal,
  at,
  inline = false,
}: RefusalLineProps<Place>) {
  if (refusal?.where !== at) return null;
  const Tag = inline ? "span" : "p";
  return (
    <Tag role="alert" className="error">
      {refusal.message}
    </Tag>
  );
}
